import { get, isEmpty } from 'lodash-es';

import {
  isMembershipTermsV02,
  MEMBERSHIP_TERMS_V01,
  MEMBERSHIP_TERMS_V02,
} from './membershipTerms';

/** Legacy nationwide membership subscription rates (v01 / pre–location pricing). */
export const V01_SUBSCRIPTION_PRICE = {
  mid: 95,
  main: 125,
};

/**
 * v02 = location catalog pricing; v01, null, or unset = grandfathered $95 / $125.
 */
export function isGrandfatheredMemberPricing(membershipDetails) {
  if (isEmpty(membershipDetails) || !get(membershipDetails, 'id')) {
    return false;
  }
  if (get(membershipDetails, 'is_v01_member_pricing') === true) {
    return true;
  }
  return !isMembershipTermsV02(membershipDetails);
}

export function getV01SubscriptionPriceForPlan(planNameOrType) {
  const normalized = String(planNameOrType || '').toLowerCase();
  if (normalized === 'mid' || normalized.includes('50')) {
    return V01_SUBSCRIPTION_PRICE.mid;
  }
  if (normalized === 'main' || normalized.includes('80')) {
    return V01_SUBSCRIPTION_PRICE.main;
  }
  return null;
}

/**
 * Monthly subscription rate for a member's plan (grandfathered or API-provided).
 */
export function resolveMemberSubscriptionPrice(
  membershipDetails,
  planNameOrType,
) {
  if (!isGrandfatheredMemberPricing(membershipDetails)) {
    return null;
  }

  const planKey = planNameOrType || get(membershipDetails, 'name');
  const isCurrentPlan =
    planKey &&
    get(membershipDetails, 'name') &&
    String(planKey).toLowerCase() ===
      String(membershipDetails.name).toLowerCase();

  if (isCurrentPlan) {
    const apiLocked = get(membershipDetails, 'locked_subscription_price');
    if (apiLocked != null && apiLocked !== '') {
      const locked = Number(apiLocked);
      if (Number.isFinite(locked)) {
        return locked;
      }
    }

    const billedPrice = get(membershipDetails, 'price');
    if (billedPrice != null && billedPrice !== '') {
      const price = Number(billedPrice);
      if (Number.isFinite(price) && price > 0) {
        return price;
      }
    }
  }

  return getV01SubscriptionPriceForPlan(
    planKey || get(membershipDetails, 'name'),
  );
}

/** @deprecated Prefer resolveMemberSubscriptionPrice; kept for call sites. */
export function getLockedSubscriptionPrice(membershipDetails) {
  if (isEmpty(membershipDetails)) {
    return null;
  }
  return resolveMemberSubscriptionPrice(
    membershipDetails,
    get(membershipDetails, 'name'),
  );
}

/**
 * Resolve the membership home location for purchase / display.
 * Never prefer a browsed studio (appointment locationServices) over an
 * explicit home, existing membership home, or user home — that stamped
 * last-browsed locations onto memberships during re-purchase.
 *
 * @param {Object} params
 * @param {*} [params.homeLocationId] session-selected home (purchase funnel)
 * @param {Object} [params.membershipDetails]
 * @param {Object} [params.userData]
 * @param {*} [params.browsedLocationId] last-resort only (e.g. guest funnel)
 */
export function getMemberLocationId({
  membershipDetails,
  homeLocationId,
  userData,
  browsedLocationId,
} = {}) {
  return (
    homeLocationId ||
    get(membershipDetails, 'location_id') ||
    get(userData, 'location_id') ||
    browsedLocationId ||
    null
  );
}

const IMPERSONATION_SESSION_KEY = 'impersonateSessionActive';

/** Admin impersonation uses a token that cannot call authenticated /services (403 scope). */
export function isImpersonationSession() {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.localStorage.getItem('impersonateUser') === 'true' &&
    window.sessionStorage.getItem(IMPERSONATION_SESSION_KEY) === 'true'
  );
}

/** GX v01 pricing is only available during an active impersonation session. */
export function isGxV01MembershipPricingEnabled(forceV01) {
  return !!forceV01 && isImpersonationSession();
}

export function shouldFetchLocationServicesWithAuth(authToken) {
  if (!authToken || isImpersonationSession()) {
    return false;
  }
  return true;
}

function applyV01PriceToService(serv, price) {
  return {
    ...serv,
    subscription_price: price,
    member_price: price,
    discounted_subscription_price: price,
  };
}

/**
 * Normalize service rows for booking display / charge preview.
 *
 * - v01: nationwide $95 Mid / $125 Main (and any auth service locks).
 * - v02: keep auth `/location/:id/services` member_price as-is. The API already
 *   applies portable home-studio rates (Studio City member at Sarasota still
 *   sees Studio City member_price). Never stamp legacy $95/$125 onto v02.
 * - non-members / empty membership: keep catalog from the response.
 */
export function applyMemberPricingToServices(services, membershipDetails) {
  if (isEmpty(services)) {
    return services;
  }

  // Active v02 (and guests): trust API portable / catalog member_price.
  // Do not honor is_v01_member_pricing or locked_subscription_price overlays —
  // those can incorrectly rewrite traveling v02 rates to $95/$125 after a
  // mid-flow location change.
  if (!isGrandfatheredMemberPricing(membershipDetails)) {
    return services;
  }

  return services.map(serv => {
    const serviceLocked = get(serv, 'locked_subscription_price');
    if (serviceLocked != null && serviceLocked !== '') {
      const locked = Number(serviceLocked);
      if (Number.isFinite(locked)) {
        return applyV01PriceToService(serv, locked);
      }
    }

    if (get(serv, 'is_v01_member_pricing') === true) {
      const v01FromService = getV01SubscriptionPriceForPlan(serv.title);
      if (v01FromService != null) {
        return applyV01PriceToService(serv, v01FromService);
      }
    }

    const v01Price = getV01SubscriptionPriceForPlan(serv.title);
    if (v01Price == null) {
      return serv;
    }

    return applyV01PriceToService(serv, v01Price);
  });
}

/** Single service row with v01 / locked pricing applied (booking, checkout). */
export function resolvePricedService(service, membershipDetails) {
  if (!service) {
    return service;
  }
  const [priced] = applyMemberPricingToServices([service], membershipDetails);
  return priced || service;
}

export function getPricingAwareServices(services, membershipDetails) {
  return applyMemberPricingToServices(services, membershipDetails);
}

/** Synthetic membership context for GX-forced v01 pricing during impersonation. */
export function buildForcedV01MembershipContext() {
  return {
    id: 1,
    membership_terms_version: MEMBERSHIP_TERMS_V01,
    is_v01_member_pricing: true,
  };
}

export function resolveMembershipPurchasePlanId(duration) {
  return `${duration}minv2`;
}

export function resolveMembershipPurchaseTermsVersion(forceV01) {
  // Create/upgrade/downgrade use v02 catalog terms unless GX forces v01.
  // Explicit v02 avoids the API inheriting grandfathered v01 on plan changes.
  return forceV01 ? MEMBERSHIP_TERMS_V01 : MEMBERSHIP_TERMS_V02;
}

export function resolveMembershipPurchasePrice(
  selectedMembershipType,
  forceV01,
) {
  const planName = get(selectedMembershipType, 'membership');
  if (forceV01 && planName) {
    const v01Price = getV01SubscriptionPriceForPlan(planName);
    if (v01Price != null) {
      return v01Price;
    }
  }
  return get(selectedMembershipType, 'price');
}

export function getMemberMonthlyDisplayPrice(membershipDetails, pricingRow) {
  const rowMembership = get(pricingRow, 'membership');
  const catalogPrice = get(pricingRow, 'price');
  const planName = rowMembership || get(membershipDetails, 'name');
  const currentName = String(
    get(membershipDetails, 'name') || '',
  ).toLowerCase();
  const plan = String(planName || '').toLowerCase();

  // Manage page: current plan shows locked billed rate for grandfathered members
  // (including paused). Other tiers are not shown here.
  if (
    isGrandfatheredMemberPricing(membershipDetails) &&
    plan &&
    currentName &&
    plan === currentName
  ) {
    const locked = resolveMemberSubscriptionPrice(membershipDetails, planName);
    if (locked != null) {
      return locked;
    }
  }
  return catalogPrice;
}

/**
 * Package-card monthly price (matches iOS BuyMembership displayedMonthlyPrice):
 * - Active v01 member, current tier: locked/billed rate ($95 / $125)
 * - Upgrade / downgrade / other tier: location catalog (never grandfathered)
 * - v02 / paused / cancelled / non-member: location catalog
 */
export function resolveDisplayedMonthlyPlanPrice({
  membershipDetails,
  planName,
  catalogPrice,
} = {}) {
  if (catalogPrice == null || catalogPrice === '') {
    return catalogPrice;
  }
  if (isEmpty(membershipDetails) || !get(membershipDetails, 'id')) {
    return catalogPrice;
  }

  const statusId = Number(get(membershipDetails, 'entity_status_id'));
  const isActive = statusId === 1;
  if (!isActive || !isGrandfatheredMemberPricing(membershipDetails)) {
    return catalogPrice;
  }

  const currentName = String(
    get(membershipDetails, 'name') || '',
  ).toLowerCase();
  const plan = String(planName || '').toLowerCase();
  if (!plan || !currentName || plan !== currentName) {
    // Downgrade / upgrade: show today's catalog, not grandfathered $95 / $125.
    return catalogPrice;
  }

  const locked = resolveMemberSubscriptionPrice(membershipDetails, planName);
  return locked != null ? locked : catalogPrice;
}
